"use client";

import React, { useEffect, useState } from "react";
import { Globe, Mail, Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 animate-pulse rounded-md bg-foreground/5" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-none bg-transparent px-4 py-2 text-sm font-default font-medium text-foreground transition-colors hover:bg-transparent hover:text-ring focus:bg-transparent focus:text-ring focus:outline-none disabled:pointer-events-none disabled:opacity-50 relative after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[1px] after:scale-x-0 after:bg-ring after:transition-transform hover:after:scale-x-100">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background border-t border-border/40 shadow-none md:hidden flex flex-col py-4 px-4 gap-4 z-50">
      {loading ? <div className="flex flex-col gap-4">
          {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-10 w-full animate-pulse rounded-none bg-foreground/5" />)}
        </div> : <nav className="flex flex-col gap-2">
          {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="flex items-center py-3 px-4 rounded-none text-base font-default font-medium text-foreground hover:bg-accent hover:text-ring transition-colors">
              {item.label}
            </Link>)}
        </nav>}
      
      <div className="flex flex-col gap-4 pt-4 border-t border-border/40 mt-2">
        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <Link to="mailto:hallo@gedankenfluss.ch" className="flex items-center gap-2 hover:text-foreground transition-colors text-foreground">
            <Mail className="size-4 text-ring" />
            hallo@gedankenfluss.ch
          </Link>
          <Link to="#" className="flex items-center gap-2 hover:text-foreground transition-colors text-foreground">
            <Phone className="size-4 text-ring" />
            Philosophische Essays
          </Link>
        </div>
        <Button className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-none font-default uppercase tracking-widest text-xs">
          Newsletter
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="252290" id="navigation" className={cn("text-foreground sticky top-0 z-50 w-full transition-all duration-700 border-b", isScrolled ? "bg-background/70 backdrop-blur-xl border-border/40 shadow-none" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-24 items-center justify-between gap-4">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <WvcLogo className="h-7 w-auto text-foreground" />
            </Link>
          </div>

          <div className="flex-1 flex justify-center">
            <MenuProvider menu_id="primary">
              <DesktopNav />
            </MenuProvider>
          </div>

          <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
            <div className="flex flex-col items-end text-[10px] font-default uppercase tracking-widest text-muted-foreground">
              <Link to="mailto:hallo@gedankenfluss.ch" className="flex items-center gap-1.5 hover:text-foreground transition-colors text-foreground">
                <Mail className="size-3 text-ring" />
                hallo@gedankenfluss.ch
              </Link>
              <Link to="/HomePage" className="flex items-center gap-1.5 hover:text-foreground transition-colors mt-0.5 text-foreground">
                <span className="size-1 rounded-full bg-ring" />
                Gedankengänge Blog
              </Link>
            </div>

            <Select defaultValue="de">
              <SelectTrigger className="w-[72px] h-8 bg-transparent border-border/40 text-foreground hover:bg-accent transition-colors focus:ring-ring/30 rounded-none text-xs">
                <Globe className="size-3 mr-1 text-ring" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border-border/40 rounded-none">
                <SelectItem value="de">DE</SelectItem>
                <SelectItem value="en">EN</SelectItem>
              </SelectContent>
            </Select>

            <Button className="bg-foreground text-background hover:bg-foreground/80 shadow-none font-default font-medium uppercase tracking-widest text-xs rounded-none px-6">
              Lesen
            </Button>
          </div>

          <div className="flex lg:hidden items-center gap-4">
            <Select defaultValue="de">
              <SelectTrigger className="w-[72px] h-8 bg-transparent border-border/40 text-foreground md:hidden rounded-none text-xs">
                <Globe className="size-3 mr-1 text-ring" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border-border/40 rounded-none">
                <SelectItem value="de">DE</SelectItem>
                <SelectItem value="en">EN</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent hover:text-foreground rounded-none" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-5 stroke-[1.5px]" /> : <Menu className="size-5 stroke-[1.5px]" />}
            </Button>
          </div>
        </div>
      </div>

      <MenuProvider menu_id="primary">
        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </section>;
}